import "react-quill/dist/quill.snow.css";

import React, { FC, useRef, useState } from "react";
import { LuPencil } from "react-icons/lu";
import ReactQuill from "react-quill";
import { useParams } from "react-router-dom";

import { WriteWithAi } from "../../../ai/WriteWithAi";
import { EMAIL_TEMPLATE_PLACEHOLDERS } from "../../../settings/templates/emailTemplates/emailTemplates.constants";

type Props = {
  title: string;
  subject: string;
  body: string;
  onClose: () => void;
  onDone: (value: { subject: string; body: string }) => void;
};

/** The pencil next to an email's template: change the wording for this sequence only. */
export const SequenceEmailEditor: FC<Props> = ({
  title,
  subject: initialSubject,
  body: initialBody,
  onClose,
  onDone,
}) => {
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [isPlaceholderOpen, setIsPlaceholderOpen] = useState(false);
  const editorRef = useRef<ReactQuill | null>(null);
  // On a work item page the AI can use the work and its client for context.
  const { workId } = useParams();

  const insertPlaceholder = (token: string) => {
    const editor = editorRef.current?.getEditor();
    if (!editor) {
      setBody((current) => `${current}${token}`);
    } else {
      const range = editor.getSelection(true);
      editor.insertText(range?.index || 0, token, "user");
    }
    setIsPlaceholderOpen(false);
  };

  return (
    <div
      className={
        "fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(20,22,34,0.45)] p-4 font-Manrope"
      }
      onClick={onClose}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={`Edit ${title}`}
        onClick={(event) => event.stopPropagation()}
        className={
          "flex max-h-[calc(100vh-48px)] w-[560px] max-w-full flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
        }>
        <div
          className={
            "flex items-center gap-2 border-b border-[#EDEFF3] px-5 py-4 text-[15px] font-extrabold text-[#1F2433]"
          }>
          <LuPencil size={15} className={"text-[#6B4EFF]"} />
          {title}
        </div>
        <div className={"min-h-0 flex-1 overflow-y-auto px-5 py-4"}>
          <label
            className={
              "flex items-center gap-3 rounded-lg border border-[#DCE2EA] px-3"
            }>
            <span className={"text-[12.5px] text-[#8A90A2]"}>Subject</span>
            <input
              aria-label={"Subject"}
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              className={
                "h-10 flex-1 bg-transparent text-[13.5px] text-[#1F2433] outline-none"
              }
            />
          </label>
          <div className={"mb-1.5 mt-4 flex items-center justify-between"}>
            <span
              className={
                "text-[11px] font-bold uppercase tracking-[0.04em] text-[#8A90A2]"
              }>
              Message
            </span>
            <div className={"relative flex items-center gap-3"}>
              <WriteWithAi
                purpose={"email"}
                getHtml={() => body}
                onUseHtml={setBody}
                subject={subject}
                onSubject={setSubject}
                workId={workId}
              />
              <button
                type={"button"}
                onClick={() => setIsPlaceholderOpen((open) => !open)}
                className={
                  "text-[12.5px] font-semibold text-[#6B4EFF] hover:underline"
                }>
                Add a placeholder
              </button>
              {isPlaceholderOpen && (
                <div
                  className={
                    "absolute right-0 top-[calc(100%+4px)] z-10 w-40 rounded-lg border border-[#ECEEF2] bg-white p-1 shadow-[0_10px_30px_rgba(20,22,34,0.16)]"
                  }>
                  {EMAIL_TEMPLATE_PLACEHOLDERS.map((placeholder) => (
                    <button
                      key={placeholder.key}
                      type={"button"}
                      onClick={() => insertPlaceholder(placeholder.token)}
                      className={
                        "block w-full rounded-md px-2.5 py-1.5 text-left text-[13px] text-[#3A4256] hover:bg-[#F4F2FF]"
                      }>
                      {placeholder.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className={"overflow-hidden rounded-lg border border-[#DCE2EA]"}>
            <ReactQuill
              ref={editorRef}
              theme={"snow"}
              value={body}
              onChange={setBody}
              className={"[&_.ql-container]:min-h-[220px]"}
            />
          </div>
        </div>
        <div
          className={
            "flex justify-end gap-2.5 border-t border-[#EDEFF3] px-5 py-3.5"
          }>
          <button
            type={"button"}
            onClick={onClose}
            className={
              "h-9 rounded-lg border border-[#DCE2EA] px-4 text-[13px] font-semibold text-[#3A4256] hover:bg-[#F7F8FA]"
            }>
            Cancel
          </button>
          <button
            type={"button"}
            disabled={!subject.trim()}
            onClick={() => onDone({ subject, body })}
            className={
              "h-9 rounded-lg bg-[#6B4EFF] px-5 text-[13px] font-bold text-white hover:bg-[#5A3EEB] disabled:opacity-50"
            }>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
